if (customElements && !customElements.get('collections-intro')) {
  class CollectionsIntro extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    /* CKC 4 Oct 2026: split into WORDS of letters. Each word is its own
       overflow-hidden box, so the slide-up still clips per word, words can
       wrap onto a new line instead of running off the screen, and the space
       between words is kept (it used to collapse: "PROJECTGEMINI"). */
    splitIntoLetters(el) {
      const esc = (c) => c.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const words = el.textContent.trim().split(/\s+/).filter(Boolean);
      el.innerHTML = words
        .map(
          (w) =>
            `<span class="collections-intro__word">${[...w]
              .map((char) => `<span class="collections-intro__letter" aria-hidden="true">${esc(char)}</span>`)
              .join('')}</span>`
        )
        .join('');
    }

    /* Stack the two halves when they would not fit side by side, and shrink
       the type if a single word is still wider than the screen. */
    fitHeading() {
      const heading = this.querySelector('.collections-intro__heading');
      if (!heading) return;
      heading.classList.remove('is-stacked');
      heading.style.fontSize = '';
      const limit = this.clientWidth * 0.92;
      if (window.innerWidth >= 1200 && heading.scrollWidth > limit) heading.classList.add('is-stacked');
      let widest = 0;
      this.querySelectorAll('.collections-intro__word').forEach((w) => {
        widest = Math.max(widest, w.scrollWidth);
      });
      if (widest > limit) {
        const fs = parseFloat(getComputedStyle(heading).fontSize);
        heading.style.fontSize = Math.floor(fs * (limit / widest)) + 'px';
      }
    }

    init() {
      if (typeof gsap === 'undefined') {
        console.error('CollectionsIntro: GSAP is not loaded');
        return;
      }

      this.querySelectorAll('.collections-intro__text-wrap').forEach((el) =>
        this.splitIntoLetters(el)
      );
      this.fitHeading();

      const letters = this.querySelectorAll('.collections-intro__letter');
      const imageBox = this.querySelector('.collections-intro__image-box');
      const growingImage = this.querySelector('.collections-intro__growing-image');
      const textStart = this.querySelector('.collections-intro__text-start');
      const textEnd = this.querySelector('.collections-intro__text-end');
      const extraImages = this.querySelectorAll('.collections-intro__image-extra');
      const title = this.querySelector('.collections-intro__title');
      const subtitle = this.querySelector('.collections-intro__subtitle');

      // Loader is hidden via CSS (opacity:0) before JS runs — restore it immediately
      const loader = this.querySelector('.collections-intro__loader');
      if (loader) gsap.set(loader, { opacity: 1 });

      // Hide final content immediately so it doesn't overlap the heading during early phases
      if (title) gsap.set(title, { yPercent: 100 });
      if (subtitle) gsap.set(subtitle, { yPercent: 100 });

      const tl = gsap.timeline({
        defaults: { ease: 'expo.inOut' },
      });

      // Phase 1: Letters slide up into view
      if (letters.length) {
        tl.from(letters, {
          yPercent: 100,
          stagger: 0.025,
          duration: 1.25,
        });
      }

      // Phase 2: Image box opens, growing image expands within it, text parts nudge apart
      if (imageBox) {
        tl.fromTo(imageBox, { width: '0em' }, { width: '1em', duration: 1.25 }, '< 1.25');
      }

      if (growingImage) {
        tl.fromTo(growingImage, { width: '0%' }, { width: '100%', duration: 1.25 }, '<');
      }

      if (textStart) {
        tl.fromTo(textStart, { x: '0em' }, { x: '-0.05em', duration: 1.25 }, '<');
      }

      if (textEnd) {
        tl.fromTo(textEnd, { x: '0em' }, { x: '0.05em', duration: 1.25 }, '<');
      }

      // Phase 3: Extras cycle DURING box opening (starts 0.3s after phase 2 begins).
      if (extraImages.length) {
        tl.fromTo(
          extraImages,
          { opacity: 1 },
          { opacity: 0, duration: 0.05, ease: 'none', stagger: 0.3 },
          '<0.3'
        );
      }

      // Phase 4: Move image-box to a direct child of collections-intro (position:relative)
      // so its absolute coordinates are simply (boxRect - sectionRect), then expand to 100%.
      // This avoids nested containing-block offsets from __heading inside __loader.
      tl.call(() => {
        if (!imageBox) return;
        const sectionRect = this.getBoundingClientRect();
        const boxRect = imageBox.getBoundingClientRect();
        const boxInner = imageBox.querySelector('.collections-intro__image-box-inner');

        // Fade the entire loader out — avoids layout jumps from individual text repositioning
        if (loader) gsap.to(loader, { opacity: 0, duration: 0.5 });

        // Reparent: insert before __content so z-index:2 stays below title (z-index:3)
        const content = this.querySelector('.collections-intro__content');
        this.insertBefore(imageBox, content || null);

        // Set exact visual starting position within collections-intro coordinate space
        gsap.set(imageBox, {
          position: 'absolute',
          left: boxRect.left - sectionRect.left,
          top: boxRect.top - sectionRect.top,
          width: boxRect.width,
          height: boxRect.height,
          overflow: 'hidden',
          zIndex: 2,
        });

        if (boxInner) gsap.set(boxInner, { width: '100%', height: '100%', minWidth: 0 });
        gsap.to(imageBox, {
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          duration: 1.8,
          ease: 'expo.inOut',
        });
      }, [], '< 1.25');

      // Phase 5: Final title and subtitle appear centered over the expanded image
      if (title) {
        tl.to(title, { yPercent: 0, opacity: 1, duration: 1.25, ease: 'expo.out' }, '< 1.2');
      }

      if (subtitle) {
        tl.to(subtitle, { yPercent: 0, opacity: 1, duration: 1.25, ease: 'expo.out' }, '<');
      }
    }
  }

  customElements.define('collections-intro', CollectionsIntro);
}
